import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  DataTable,
  Input,
  type Column,
  type SortState,
} from '../ui'
import { cn } from '../../shared/lib/cn'
import {
  csvFromTableRows,
  filterRows,
  isNumericColumn,
  normalizeRows,
  resolveColumns,
  sortRows,
  type CsvTableRow,
  type NormalizedCsvRow,
} from './csvTableUtils'

type RenderCsvRow = NormalizedCsvRow & { __rowId: string }

interface CsvTableViewerProps {
  title: string
  description?: string
  rows: CsvTableRow[]
  columns?: string[]
  filename?: string
  emptyMessage?: string
  pageSizeOptions?: number[]
  initialPageSize?: number
  maxHeight?: string
  compact?: boolean
  className?: string
  toolbar?: React.ReactNode
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function CsvTableViewer({
  title,
  description,
  rows,
  columns: preferredColumns,
  filename = 'table.csv',
  emptyMessage = 'No rows to display',
  pageSizeOptions = [10, 25, 50, 100],
  initialPageSize = 10,
  maxHeight = '520px',
  compact = true,
  className,
  toolbar,
}: CsvTableViewerProps) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null })

  const resolvedColumns = useMemo(
    () => resolveColumns(rows, preferredColumns),
    [rows, preferredColumns],
  )
  const normalizedRows = useMemo(
    () => normalizeRows(rows, resolvedColumns),
    [rows, resolvedColumns],
  )
  const filteredRows = useMemo(
    () => filterRows(normalizedRows, query),
    [normalizedRows, query],
  )
  const sortedRows = useMemo(
    () => sortRows(filteredRows, sortState),
    [filteredRows, sortState],
  )
  const tableRows = useMemo<RenderCsvRow[]>(
    () => sortedRows.map((row, index) => ({ ...row, __rowId: String(index) })),
    [sortedRows],
  )

  useEffect(() => {
    setPage(1)
  }, [query, pageSize, sortState.column, sortState.direction])

  const tableColumns = useMemo<Column<RenderCsvRow>[]>(
    () =>
      resolvedColumns.map(column => {
        const numeric = isNumericColumn(normalizedRows, column)
        return {
          id: column,
          header: column,
          accessorFn: row => row[column],
          sortable: true,
          align: numeric ? 'right' : 'left',
          minWidth: numeric ? '120px' : '160px',
          cellClassName: cn(
            numeric && 'font-mono font-medium tabular-nums',
            column.toLowerCase().includes('memo') && 'max-w-[520px]',
          ),
          cell: ({ value }) => (
            <span className={cn(column.toLowerCase().includes('memo') && 'line-clamp-2')}>
              {String(value ?? '')}
            </span>
          ),
        }
      }),
    [normalizedRows, resolvedColumns],
  )

  const exportRows = sortedRows
  const visibleCount = sortedRows.length

  return (
    <section
      className={cn(
        'overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface',
        className,
      )}
    >
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-4 dark:border-dark-border xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <TableCellsIcon className="h-5 w-5 text-gray-400" />
            <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">
              {title}
            </h2>
            <Badge variant="secondary">
              {visibleCount} of {normalizedRows.length} rows
            </Badge>
          </div>
          {description ? (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {toolbar}
          <label className="relative block min-w-[220px]">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search rows"
              className="pl-9"
            />
          </label>
          <select
            value={pageSize}
            onChange={event => setPageSize(Number(event.target.value))}
            className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal shadow-sm transition-colors focus:border-eliza-red focus:outline-none focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
            aria-label="Rows per page"
          >
            {pageSizeOptions.map(option => (
              <option key={option} value={option}>
                {option} rows
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              downloadText(
                filename,
                csvFromTableRows(exportRows, resolvedColumns),
                'text/csv;charset=utf-8',
              )
            }
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </Button>
        </div>
      </div>

      <div className="p-4">
        <DataTable
          data={tableRows}
          columns={tableColumns}
          getRowId={row => row.__rowId}
          sortable
          sortState={sortState}
          onSortChange={setSortState}
          paginated
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          stickyHeader
          maxHeight={maxHeight}
          compact={compact}
          striped
          bordered
          emptyMessage={emptyMessage}
        />
      </div>
    </section>
  )
}
